import * as React from 'react';
import { toast } from 'sonner';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  ApiErrorCode,
  parseApiError,
  COUNTIES_BY_VOIVODESHIP,
  VOIVODESHIPS,
  MATERIAL_VALUES,
  LIGHTING_VALUES,
  mediaTypeRequiresDimensions,
  type MediaUnitDetail,
} from '@ooh/contracts';
import { suppliersQuery, formatsQuery, type Supplier, type MediaFormat } from '../api/queries';
import { useReplaceTagsMutation } from '../api/mutations';
import { multipartFetch } from '../api/multipart-fetch';
import { createFormSchema, type FormValues } from '../../../lib/media-unit-form-schema';
import { computedArea, buildUpdateDto, toValidDictValue } from '../lib/form-helpers';
import { partitionPhotoSelection } from '@ooh/screens';
import type { ExistingPhoto, NewPhoto } from '@ooh/screens';

// eslint-disable-next-line max-lines-per-function, complexity
export function useEditForm(
  unit: MediaUnitDetail,
  id: string,
  catalogSearch?: string,
  canViewSupplier = true,
) {
  const detailSearch = catalogSearch != null ? { catalogSearch } : undefined;

  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const { auth } = useRouteContext({ from: '__root__' });

  const [existingPhotos, setExistingPhotos] = React.useState<ExistingPhoto[]>(
    // ExistingPhoto.url is the photo-manager tile's display source (thumb rendition) —
    // not the original. See client-offer-review's parallel thumbnailUrl/displayUrl split.
    unit.photos.map((p) => ({
      id: p.id,
      url: p.thumbnailUrl,
      originalName: p.originalName,
      storageKey: p.storageKey,
    })),
  );
  const [newPhotos, setNewPhotos] = React.useState<NewPhoto[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = React.useState<number[]>([]);
  const newPhotosRef = React.useRef<NewPhoto[]>([]);
  const [photoCountError, setPhotoCountError] = React.useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const saveErrorMessage = t('form.saveErrorMessage');

  const { data: suppliersData } = useQuery(suppliersQuery(auth));
  const { data: formatsData } = useQuery(formatsQuery(auth));
  // suppliersQuery returns SupplierListItem[] (plain array — no .data wrapper)
  const suppliers: Supplier[] = suppliersData ?? [];
  const formats: MediaFormat[] = (formatsData as { data?: MediaFormat[] })?.data ?? [];
  const supplierOptions = suppliers.map((s) => ({ value: String(s.id), label: s.name }));
  const formatOptions = formats.map((f) => ({ value: String(f.id), label: f.label }));

  // unit.mediaType is nullable only for a unit with no variants (data anomaly);
  // fall back to the dimension-required default rather than silently skipping
  // width/height validation for an unknown type.
  const unitMediaType = unit.mediaType ?? 'billboard';
  const schema = React.useMemo(
    () => createFormSchema(t, canViewSupplier, unitMediaType),
    [t, canViewSupplier, unitMediaType],
  );
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      supplierNumber: unit.supplierNumber ?? '',
      supplierId: unit.supplierId != null ? String(unit.supplierId) : '',
      formatId: unit.formatId != null ? String(unit.formatId) : '',
      regionCode: unit.region ?? '',
      countyName: unit.county ?? '',
      agglomeration: unit.agglomeration ?? '',
      city: unit.city,
      address: unit.address ?? '',
      widthM: unit.widthM != null ? String(unit.widthM) : '',
      heightM: unit.heightM != null ? String(unit.heightM) : '',
      material: toValidDictValue(MATERIAL_VALUES, unit.material),
      lighting: toValidDictValue(LIGHTING_VALUES, unit.lighting),
      latitude: unit.latitude != null ? String(unit.latitude) : '',
      longitude: unit.longitude != null ? String(unit.longitude) : '',
      notes: unit.notes ?? '',
      wfContactPerson: unit.wfContactPerson ?? '',
      locationDescription: unit.locationDescription ?? '',
      catalogPrice: unit.catalogPrice != null ? String(unit.catalogPrice) : '',
      rent: unit.rent != null ? String(unit.rent) : '',
      productionCost: unit.productionCost != null ? String(unit.productionCost) : '',
      installCost: unit.installCost != null ? String(unit.installCost) : '',
      dismantlingCost: unit.dismantlingCost != null ? String(unit.dismantlingCost) : '',
      exposureCost: unit.exposureCost != null ? String(unit.exposureCost) : '',
      additionalCosts: unit.additionalCosts != null ? String(unit.additionalCosts) : '',
      discountedPrice: unit.discountedPrice != null ? String(unit.discountedPrice) : '',
      lastMinutePrice: unit.lastMinutePrice != null ? String(unit.lastMinutePrice) : '',
      starterPrice: unit.starterPrice != null ? String(unit.starterPrice) : '',
      clientProduction: unit.clientProduction != null ? String(unit.clientProduction) : '',
      clientInstallation: unit.clientInstallation != null ? String(unit.clientInstallation) : '',
      clientDismantling: unit.clientDismantling != null ? String(unit.clientDismantling) : '',
      clientAdditionalFees:
        unit.clientAdditionalFees != null ? String(unit.clientAdditionalFees) : '',
      tagDefinitionIds: unit.tags?.map((tg) => tg.id) ?? [],
    },
  });

  const regionCode = watch('regionCode');
  const area = computedArea(watch('widthM'), watch('heightM'));
  const filteredCounties = regionCode
    ? (COUNTIES_BY_VOIVODESHIP[regionCode] ?? []).map((c) => ({ value: c.value, label: c.label }))
    : [];

  const handleRegionChange = React.useCallback(
    (value: string) => {
      setValue('regionCode', value, { shouldDirty: true });
      setValue('countyName', '', { shouldDirty: true });
      setValue('agglomeration', '', { shouldDirty: true });
    },
    [setValue],
  );

  const handleFileChange = React.useCallback(
    (files: FileList | File[]) => {
      const allFiles = Array.from(files);
      const { accepted, error } = partitionPhotoSelection({
        selectedFiles: allFiles,
        currentCount: existingPhotos.length + newPhotos.length,
        t,
      });
      setPhotoCountError(error);
      if (accepted.length > 0) {
        setNewPhotos((prev) => [
          ...prev,
          ...accepted.map((file) => ({
            id: crypto.randomUUID(),
            file,
            url: URL.createObjectURL(file),
          })),
        ]);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [existingPhotos.length, newPhotos.length, t],
  );

  const handleRemoveExistingPhoto = React.useCallback((photoId: number) => {
    setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
    setDeletedPhotoIds((prev) => [...prev, photoId]);
  }, []);

  const handleRemoveNewPhoto = React.useCallback((photoId: string) => {
    setNewPhotos((prev) => {
      const updated = prev.filter((p) => p.id !== photoId);
      const removed = prev.find((p) => p.id === photoId);
      if (removed) URL.revokeObjectURL(removed.url);
      return updated;
    });
  }, []);

  React.useEffect(() => {
    newPhotosRef.current = newPhotos;
  }, [newPhotos]);
  React.useEffect(() => {
    return () => {
      newPhotosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, []);

  const isFormDirty = isDirty || newPhotos.length > 0 || deletedPhotoIds.length > 0;

  const replaceTagsMutation = useReplaceTagsMutation();

  const { mutate: updateUnit, isPending: isPatchPending } = useMutation({
    mutationFn: async (values: FormValues) => {
      const dto = buildUpdateDto(values, deletedPhotoIds);
      const formData = new FormData();
      formData.append('data', JSON.stringify(dto));
      newPhotosRef.current.forEach((p) => formData.append('photos', p.file));
      const token = auth.accessToken;
      let response: Response;
      try {
        response = await multipartFetch(`/media-units/${id}`, 'PATCH', formData, token);
      } catch {
        throw new Error(saveErrorMessage);
      }
      const body: unknown = await response.json().catch(() => ({}));
      if (!response.ok) throw new ApiError(response.status, response.statusText, body);
      return body;
    },
    onSuccess: async (_data, variables) => {
      const tagDefinitionIds = variables.tagDefinitionIds ?? [];
      try {
        await replaceTagsMutation.mutateAsync({ id: Number(id), tagDefinitionIds });
        toast.success(t('form.edit.successTitle'));
        void navigate({ to: '/media-units/$id', params: { id }, search: detailSearch });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('form.saveButton.error'));
      }
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      if (parsed.errorCode === ApiErrorCode.SUPPLIER_NUMBER_COLLISION) {
        setError('supplierNumber', { message: t('form.supplierNumberCollision') });
        return;
      }
      toast.error(saveErrorMessage);
    },
  });

  const isPending = isPatchPending || replaceTagsMutation.isPending;

  const handleCancel = () => {
    if (isFormDirty) {
      setShowDiscardConfirm(true);
    } else {
      void navigate({ to: '/media-units/$id', params: { id }, search: detailSearch });
    }
  };

  // Surfaces feedback when whole-unit validation blocks the submit. Without this
  // react-hook-form aborts silently, so the user sees no toast and the offending
  // field may be off-screen. shouldFocusError (on by default) scrolls to the
  // first invalid field; the toast guarantees the user always gets a signal.
  const onInvalidSubmit = React.useCallback(() => {
    toast.error(t('form.validation.requiredFieldsMissing'));
  }, [t]);

  return {
    t,
    register,
    watch,
    setValue,
    errors,
    handleSubmit,
    onInvalidSubmit,
    updateUnit,
    isPending,
    isFormDirty,
    showDiscardConfirm,
    setShowDiscardConfirm,
    existingPhotos,
    newPhotos,
    deletedPhotoIds,
    fileInputRef,
    photoCountError,
    handleFileChange,
    handleRemoveExistingPhoto,
    handleRemoveNewPhoto,
    supplierOptions,
    formatOptions,
    filteredCounties,
    handleRegionChange,
    area,
    voivodeshipOptions: VOIVODESHIPS.map((v) => ({ value: v.value, label: v.label })),
    handleCancel,
    navigate,
    isDimensionRequired: mediaTypeRequiresDimensions(unitMediaType),
  };
}
